import React, { FocusEvent, KeyboardEvent, MutableRefObject, useEffect } from 'react';
import CollapsableBar from './CollapsableBar';
import { clearSearchByName, searchLocationByName, setLocation } from 'store/slices/location';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { Box, Button, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { useTheme } from '@mui/material/styles';
import { moveMapTo, MoveMapToProps, setSearchedLocationMarker } from 'store/slices/map';
import { hideSearchBar, showSubsection } from 'store/slices/searchBar';
import { storeHome } from 'store/slices/home';
import { Map } from 'mapbox-gl';
import { showSingletonToast } from 'utils/toastSingleton';

export interface SearchBarProps {
    mapRef: MutableRefObject<Map | null>;
}

const SearchBar = (props: SearchBarProps) => {
    const theme = useTheme();
    const dispatch = useAppDispatch();

    const searchBar = useAppSelector((state) => state.searchBar);
    const map = useAppSelector((state) => state.map);
    const locationData = useAppSelector((state) => state.location);

    const {
        isOpen: isSearchBarOpen,
        isSubsectionOpen,
        isSearchForHome,
        overrideNoHomeRedirect
    } = searchBar;
    const {
        searchingLocationByName,
        locationByNameError,
        locationByNameSuccess,
        searchError,
        places,
        searchKey
    } = locationData;
    const { isContextualMenuOpen } = map;

    const showSearchResult = isSubsectionOpen
        ? searchingLocationByName || locationByNameError || locationByNameSuccess
        : undefined;

    useEffect(() => {
        if (isContextualMenuOpen) {
            hideSearch();
        }
    }, [isContextualMenuOpen]);

    const search = () => {
        void dispatch(searchLocationByName({ location: searchKey }));
        void dispatch(showSubsection());
    };

    const flyTo = (spot: MoveMapToProps) => {
        dispatch(moveMapTo(spot));
    };

    const hideSearch = () => {
        dispatch(clearSearchByName());
        dispatch(hideSearchBar());
    };

    const handleLocationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setLocation(event.target.value));
    };

    const handleLocationFocus = (event: FocusEvent<HTMLInputElement>) => {
        event.target.select();
    };

    const handleLocationKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.charCode === 13) {
            search();
            event.preventDefault();
        }
    };

    return (
        <Box sx={{
            position: 'relative',
            zIndex: theme.zIndex.appBar + 1,
            width: '100%',
        }}
        >
            <Box sx={{
                position: 'absolute',
                width: '100%',
            }}
            >
                <CollapsableBar
                    isOpen={isSearchBarOpen}
                    isSubSectionOpen={showSearchResult}
                    onClose={() => hideSearch()}
                    type={'top'}
                    top={0}
                    subSection={
                        <>
                            {searchingLocationByName ? (
                                <Box>
                                    <Typography variant={'h4'}>Loading...</Typography>
                                </Box>
                            ) : null}
                            {locationByNameError ? (
                                <Box>
                                    <Typography variant={'h4'}>Please try again.</Typography>
                                    <Typography variant={'body1'}>{searchError ?? 'There was an error.'}</Typography>
                                </Box>
                            ) : null}
                            {locationByNameSuccess && places && Array.isArray(places) ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                    <Box sx={{ flex: '1', mt: 3 }}>
                                        {places.map((p) => {
                                            return (
                                                <Box key={JSON.stringify(p.id)}>
                                                    <Box m={1}>
                                                        <Button
                                                            variant='contained'
                                                            size={'small'}
                                                            onClick={() => {
                                                                hideSearch();
                                                                const currentZoom = props.mapRef?.current?.getZoom() || 12;
                                                                const lat = p.center[1];
                                                                const lng = p.center[0];

                                                                flyTo({ center: { lat, lng }, zoom: currentZoom });
                                                                dispatch(setSearchedLocationMarker({
                                                                    lat,
                                                                    lng,
                                                                    name: p.place_name
                                                                }));

                                                                if (isSearchForHome) {
                                                                    showSingletonToast('Storing new home...', {
                                                                        isLoading: true,
                                                                        autoClose: false
                                                                    });

                                                                    void dispatch(
                                                                        storeHome({
                                                                            latitude: lat,
                                                                            longitude: lng,
                                                                            zoom: currentZoom,
                                                                        })
                                                                    )
                                                                        .unwrap()
                                                                        .then(() => {
                                                                            showSingletonToast('The new home location has been set', {
                                                                                type: 'success',
                                                                                isLoading: false,
                                                                                autoClose: 3000,
                                                                            });
                                                                        })
                                                                        .catch(() => {
                                                                            showSingletonToast(
                                                                                'Error occurred while storing the new home. ' +
                                                                                'If the problem persists, please contact us.',
                                                                                {
                                                                                    type: 'error',
                                                                                    isLoading: false,
                                                                                    autoClose: 5000,
                                                                                }
                                                                            );
                                                                        });
                                                                }

                                                            }}
                                                            sx={{
                                                                maxWidth: '90vw',
                                                                textOverflow: 'wrap',
                                                                overflow: 'hidden',
                                                                minWidth: {
                                                                    xs: '90vw',
                                                                    sm: 'auto'
                                                                },
                                                                minHeight: {
                                                                    xs: '60px',
                                                                    sm: 'auto'
                                                                }
                                                            }}
                                                        >
                                                            <Typography
                                                                variant='inherit'
                                                                sx={{
                                                                    fontSize: {
                                                                        xs: '.75em',
                                                                        sm: '1em',
                                                                    },
                                                                    fontWeight: 400
                                                                }}
                                                            >
                                                                {p.place_name}
                                                            </Typography>
                                                        </Button>
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                    <Box sx={{
                                        py: 2
                                    }}
                                    >
                                        <Button
                                            variant='contained'
                                            color={'secondary'}
                                            onClick={() => {
                                                dispatch(clearSearchByName());
                                            }}
                                        >
                                            Clear search
                                        </Button>
                                    </Box>
                                </Box>
                            ) : null}
                        </>
                    }
                >
                    {!locationByNameSuccess || !places ?
                        <>
                            <Box
                                sx={{
                                    maxWidth: '860px',
                                    margin: '0 auto'
                                }}
                            >
                                <Grid container alignItems='center' my={2}>
                                    <Grid xs={12} md={9} sx={{
                                        pr: {
                                            xs: 0,
                                            md: '1em'
                                        },
                                        pb: {
                                            xs: '1em',
                                            md: 0
                                        }
                                    }}
                                    >
                                        <TextField
                                            fullWidth
                                            label='Search by Location'
                                            type='text'
                                            size='medium'
                                            value={searchKey}
                                            onChange={handleLocationChange}
                                            onFocus={handleLocationFocus}
                                            onKeyPress={handleLocationKeyPress}
                                            placeholder='Enter a location, such as a country, region, or city'
                                            sx={{
                                                'input': {
                                                    'height': '32px',
                                                    'fontSize': '22px',
                                                },
                                                '& .MuiInputLabel-root': {
                                                    'fontSize': '22px',
                                                    'color': '#999',
                                                    '&.Mui-focused': {
                                                        'fontSize': '1em',
                                                    }
                                                },
                                            }}
                                        />
                                    </Grid>
                                    <Grid xs={12} md={3} sx={{ flexGrow: 1 }}>
                                        <Button
                                            fullWidth
                                            variant='contained'
                                            color='secondary'
                                            onClick={search}
                                            sx={{
                                                py: 1.5,
                                                flexGrow: 1,
                                                maxWidth: '400px'
                                            }}
                                        >
                                            Search
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Box>
                            {isSearchForHome ? (
                                <Box my={2}>
                                    <Typography variant="body1">
                                        {'To set a new Home, either click anywhere on the map and select "Set Home", ' +
                                            'or search for a location and click the result you want to use.'}
                                    </Typography>
                                    <Typography variant="body1">
                                        {'If your Home isn’t visible, try searching for it first.'}
                                    </Typography>
                                </Box>
                            ) : overrideNoHomeRedirect ? (
                                <Box my={2}>
                                    <Typography variant={'body1'}>
                                        {'Choose a location to set as your \'Home\' for the session. You can always modify it later.'}
                                    </Typography>
                                </Box>
                            ) : null}
                        </> : null}
                </CollapsableBar>
            </Box>
        </Box>
    );
};

export default SearchBar;
